/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CameraAltOutlined from "@mui/icons-material/CameraAltOutlined";
import { useDispatch } from "react-redux";
import {
  Avatar,
  Box,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { INPUT_LABELS, PROFILE_PAGE } from "../../constants/lang";
import { GENDER_OPTIONS } from "../../constants/optionsConfig";
import { COUNTRIES } from "../../constants/countryCode";
import { editProfileSchema } from "../../schema/signupSchema";
import {
  fetchProfile,
  updateProfile,
  fetchSizes,
  uploadImage,
  uploadImageToS3,
} from "../../store/reducers/home/apiThunk";
import {
  ToastNotifyError,
  ToastNotifySuccess,
} from "../../components/CommonComponents/Toast/ToastNotify";
import { updateLoading } from "../../store/reducers/loader";
import { ROUTE_PATH } from "../../constants/route";
import profileicon from "../../assets/icons/profile-new.svg";
import ImageCropper from "../../components/CommonComponents/ImageCropper/index.jsx";
import { ACCEPT_OPTIONS } from "../../constants/optionsConfig";
import StyledEditProfile from "./StyledEditProfile";
import ProfileForm from "../../components/CommonComponents/ProfileForm/index.jsx";

const EditProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizes, setSizes] = useState([]);
  const popupRef = useRef(null);
  const avatarRef = useRef(null);
  const fileInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState(profileicon);
  const [openCropperModal, setOpenCropperModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [croppedImage, setCroppedImage] = useState(profileicon);
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getProfile = async () => {
    try {
      const response = await dispatch(fetchProfile());
      const { data, message, status } = response?.payload;
      if (status) {
        setProfileData(data[0]);
      } else {
        ToastNotifyError(message);
      }
    } catch {
      ToastNotifyError("Failed to fetch profile");
    }
  };

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    setFocus,
  } = useForm({
    resolver: yupResolver(editProfileSchema),
  });

  const onSubmit = async (data) => {
    updateLoading(true);
    const capitalizeToogle =
      String(data?.toggle_hide_size || "")
        .charAt(0)
        .toUpperCase() +
      String(data?.toggle_hide_size || "")
        .slice(1)
        .toLowerCase();
    const dataToSend = {
      username: data?.username || data?.name,
      clothing_preference: data?.clothing_preference || data?.clothingPreference,
      sizes: data?.sizes,
      age: data?.age,
      country_code: data?.country_code,
      toggle_hide_size: capitalizeToogle,
      mobile_number: data?.mobile_number,
      profile_picture: (profileImage === profileicon || profileImage?.includes('profile-new.svg')) ? null : profileImage,
      location: data?.location,
    };
    try {
      const res = await dispatch(updateProfile(dataToSend));
      if (res?.payload?.status) {
        ToastNotifySuccess("Profile updated successfully");
        getProfile();
        updateLoading(false);
      } else {
        ToastNotifyError(res?.payload?.message);
        updateLoading(false);
      }
    } catch {
      ToastNotifyError("Failed to update profile");
    } finally {
      updateLoading(false);
    }
  };

  useEffect(() => {
    setValue(
      "country_code",
      COUNTRIES.find((country) => country.dial_code === "+1"),
    );
  }, [setValue]);

  const getSizes = async () => {
    try {
      const response = await dispatch(fetchSizes());
      const { data, status } = response?.payload;
      if (status && Array.isArray(data)) {
        setSizes(data);
      } else {
        ToastNotifyError("Failed to fetch sizes");
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
      ToastNotifyError("Failed to fetch sizes");
    }
  };

  useEffect(() => {
    getProfile();
    getSizes();
  }, []);

  useEffect(() => {
    setValue("name", profileData?.username);
    setValue("age", profileData?.age);
    setValue("sizes", profileData?.sizes);
    setValue("mobile_number", profileData?.mobile_number);
    setValue(
      "clothingPreference",
      GENDER_OPTIONS.find((opt) => opt.key === profileData?.clothing_preference)
        ?.key || "",
    );
    setValue(
      "toggle_hide_size",
      String(profileData?.toggle_hide_size).toLowerCase() === "true"
        ? "true"
        : "false",
    );
    if (profileData?.profile_picture) {
      setCroppedImage(profileData.profile_picture);
      setProfileImage(profileData.profile_picture);
    } else {
      setCroppedImage(profileicon);
      setProfileImage(profileicon);
    }
    setValue("location", profileData?.location);
    // Handle country_code from backend
    if (profileData?.country_code) {
      if (typeof profileData.country_code === "string") {
        // Backend returns just dial code string (e.g., "+93")
        const country = COUNTRIES.find((c) => c.dial_code === profileData.country_code);
        if (country) {
          setValue("country_code", country);
        }
      } else {
        // Backend returns full country object
        setValue("country_code", profileData.country_code);
      }
    }
  }, [profileData, setValue]);

  const togglePopup = (event) => {
    event.stopPropagation();
    if (popupRef.current) {
      popupRef.current.style.display =
        popupRef.current.style.display === "block" ? "none" : "block";
    }
  };

  const removeProfile = () => {
    setCroppedImage(profileicon);
    setProfileImage(profileicon);
    setImageUrl(null);
    popupRef.current.style.display = "none";
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ACCEPT_OPTIONS.includes(file.type)) {
      ToastNotifyError(
        "Invalid file type! Please select a PNG, JPEG, JPG, or WEBP image.",
      );
      e.target.value = "";
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      ToastNotifyError("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageUrl(reader.result);
      setOpenCropperModal(true);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleClickOutside = (event) => {
    if (
      popupRef.current &&
      !popupRef.current.contains(event.target) &&
      avatarRef.current !== event.target
    ) {
      popupRef.current.style.display = "none";
    }
  };

  const handleClick = (event) => {
    handleClickOutside(event);
  };

  const handleCancelCropper = () => {
    setOpenCropperModal(false);
    setImageUrl(null);
    popupRef.current.style.display = "none";
  };

  const handleImageCrop = ({ base64, file }) => {
    setLoading(true); // Set loading to true before changing the image
    setCroppedImage(base64);
    setCroppedImageFile(file);
    setImageUrl(null);
    setOpenCropperModal(false);
  };

  useEffect(() => {
    if (croppedImageFile !== null) {
      handleImageUpload();
    }
  }, [croppedImageFile]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  const handleImageUpload = async (e) => {
    setLoading(true);

    try {
      const response = await dispatch(uploadImage());

      if (response?.payload?.status) {
        const { upload_url, key } = response?.payload?.data;
        const uploadResponse = await dispatch(
          uploadImageToS3({
            uploadUrl: upload_url,
            file: croppedImageFile,
          }),
        );

        if (uploadResponse?.payload?.status === 200) {
          setProfileImage(process.env.REACT_APP_S3_IMAGES_URL + key);
          ToastNotifySuccess("Image uploaded successfully!");
          setLoading(false);
        } else {
          ToastNotifyError("Failed to upload the image to S3.");
        }
      } else {
        ToastNotifyError("Failed to get the upload URL.");
      }
    } catch (error) {
      ToastNotifyError("There was an error uploading the image.");
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(errors)?.length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        setFocus(firstErrorField);
        // Increase timeout to give DOM more time to update
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 500); // Increased from 300ms to 500ms
      }
    }
  }, [errors, setFocus]);

  return (
    <StyledEditProfile>
      <Box className="edit-profile-container">
        <Box className="back-btn-edit-profile">
          <ArrowBackIcon
            sx={{ fontSize: "20px", cursor: "pointer" }}
            onClick={() => navigate(ROUTE_PATH.USER_PROFILE)}
          />
          <Typography className="order-history-title">Edit Profile</Typography>
        </Box>
        <Box
          className="edit-profile-form-section"
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box
            position="relative"
            className="avatar-container"
            onClick={(e) => handleClick(e)}
          >
            {loading && (
              <Box className="loader-container">
                <CircularProgress size={40} />
              </Box>
            )}
            <>
              <Avatar
                ref={avatarRef}
                src={croppedImage}
                alt="Profile Picture"
                sx={{
                  position: "relative",
                  width: "158px",
                  height: "160px",
                  objectFit: "cover",
                  margin: "0 auto",
                }}
                imgProps={{
                  onLoad: () => setLoading(false),
                  onError: () => setLoading(false),
                }}
                onClick={(e) => togglePopup(e)}
              />
              <Box onClick={(e) => togglePopup(e)} className="camera-icon">
                <CameraAltOutlined />
              </Box>
            </>

            <input
              type="file"
              ref={fileInputRef}
              accept={ACCEPT_OPTIONS}
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            {/* Popup Menu */}
            <Box ref={popupRef} className="popup-menu">
              {/* Add/Change Profile */}
              <Box
                onClick={() => fileInputRef.current.click()}
                className="menu-item"
                aria-label={
                  croppedImage === profileicon
                    ? "Add profile picture"
                    : "Change profile picture"
                }
              >
                <ImageOutlinedIcon className="menu-icon" />
                <Typography className="change-profile">
                  {croppedImage === profileicon
                    ? "Add Profile picture"
                    : PROFILE_PAGE.changeProfile}
                </Typography>
              </Box>

              {/* Remove Profile - Only show if there's an existing image */}
              {croppedImage !== profileicon && (
                <Box
                  onClick={() => removeProfile()}
                  className="menu-item remove"
                >
                  <DeleteOutlineOutlinedIcon className="menu-icon" />
                  <Typography className="remove-profile">
                    {PROFILE_PAGE.RemoveProfile}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        
        <ProfileForm
          onSubmit={onSubmit}
          schema={editProfileSchema}
          defaultValues={profileData ? {
            name: profileData?.username,
            age: profileData?.age,
            clothingPreference: profileData?.clothing_preference,
            sizes: profileData?.sizes,
            toggle_hide_size: String(profileData?.toggle_hide_size).toLowerCase() === "true" ? "true" : "false",
            mobile_number: profileData?.mobile_number,
            location: profileData?.location,
            country_code: profileData?.country_code
          } : {}}
          submitButtonText="Save changes"
          sizes={sizes}
          profileImage={profileImage}
          setProfileImage={setProfileImage}
          profileicon={profileicon}
          isEdit={true}
          PROFILE_PAGE={PROFILE_PAGE}
          INPUT_LABELS={INPUT_LABELS}
        />
      </Box>
      <Modal
        open={openCropperModal}
        onClose={() => setOpenCropperModal(false)}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "40%",
            height: "60%",
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 3,
            padding: 2,
          }}
        >
          <Box
            className="image-crop-display"
            sx={{ height: "90%", width: "100%" }}
          >
            {imageUrl && (
              <ImageCropper
                src={imageUrl}
                onCancel={handleCancelCropper}
                onCropComplete={handleImageCrop}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </StyledEditProfile>
  );
};

export default EditProfile;
